"use client";

import { useEffect } from "react";

/**
 * Reports the full document height to the parent frame via postMessage.
 * Used when the exported page is embedded as an iframe in Sitecore.
 *
 * The page has `min-h-screen flex flex-col` on the wrapper, `min-h-full` on
 * body, and `h-full` on html — all of which lock to the iframe's viewport.
 * Naively reading `scrollHeight` returns the *viewport* size, not the true
 * content size (circular dependency: parent sets height from our report,
 * our report reflects that height).
 *
 * Solution (same as LivePageRender in the editor): temporarily strip the
 * layout constraints, force a synchronous reflow, measure, then restore.
 * All synchronous — no visual flicker.
 */
export function IframeHeightReporter() {
  useEffect(() => {
    const inIframe = window.self !== window.top;

    // Intercept hash-link clicks. In an iframe, native anchor scrolling
    // doesn't work because the parent page owns the scrollbar — we send
    // a postMessage instead. Outside an iframe, use scrollIntoView.
    const onHashClick = (e: MouseEvent) => {
      const anchor = (e.target as Element).closest?.("a[href^='#']");
      if (!anchor) return;
      const id = anchor.getAttribute("href")!.slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      // Strip hash from URL so pull-to-refresh doesn't auto-scroll back here.
      history.replaceState(null, "", location.pathname + location.search);

      if (inIframe) {
        const top = target.getBoundingClientRect().top + window.scrollY;
        window.parent.postMessage(
          { type: "cl-ciab-scroll", top: Math.round(top) },
          "*",
        );
      } else {
        target.scrollIntoView({ behavior: "smooth" });
      }
    };
    document.addEventListener("click", onHashClick);

    // If the page loaded with a #hash (e.g. user clicked before hydration,
    // or Chrome restored a hash from a previous session), clear it so
    // pull-to-refresh doesn't auto-scroll.
    if (location.hash) {
      history.replaceState(null, "", location.pathname + location.search);
    }

    if (!inIframe) {
      return () => document.removeEventListener("click", onHashClick);
    }

    // Prevent Chrome from restoring the iframe's internal scroll position
    // on pull-to-refresh or back/forward navigation.
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    let lastHeight = 0;
    let lastWidth = window.innerWidth;

    /**
     * Measure the true content height by temporarily collapsing the layout
     * constraints that lock to the iframe viewport.
     */
    const measureHeight = (): number => {
      // The wrapper is the div with min-h-screen inside body. It may NOT be
      // firstElementChild — React can insert hidden internal nodes before it.
      const wrapper = document.body.querySelector(
        ":scope > div:not([hidden])",
      ) as HTMLElement | null;
      const main = wrapper?.querySelector("main") as HTMLElement | null;

      // Save originals
      const htmlH = document.documentElement.style.height;
      const bodyMH = document.body.style.minHeight;
      const bodyD = document.body.style.display;

      // Collapse constraints
      document.documentElement.style.height = "auto";
      document.body.style.minHeight = "0px";
      document.body.style.display = "block";
      if (wrapper) {
        wrapper.style.minHeight = "0px";
        wrapper.style.display = "block";
      }
      if (main) main.style.flex = "0 0 auto";

      // Force synchronous reflow then measure
      void document.body.offsetHeight;
      const h = document.body.scrollHeight;

      // Restore — Tailwind classes take back over
      document.documentElement.style.height = htmlH;
      document.body.style.minHeight = bodyMH;
      document.body.style.display = bodyD;
      if (wrapper) {
        wrapper.style.minHeight = "";
        wrapper.style.display = "";
      }
      if (main) main.style.flex = "";

      return h;
    };

    const sendHeight = () => {
      const h = measureHeight();
      if (h === lastHeight) return;
      lastHeight = h;
      window.parent.postMessage({ type: "cl-ciab-height", height: h }, "*");
    };

    const onResize = () => {
      const w = window.innerWidth;
      if (w === lastWidth) return;
      lastWidth = w;

      // Width changed (breakpoint shift) — tell parent to collapse the iframe
      // so content reflows naturally, then re-measure.
      window.parent.postMessage({ type: "cl-ciab-recalc" }, "*");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          lastHeight = 0; // force send even if same value
          sendHeight();
        });
      });
    };

    // Initial measurement after hydration.
    sendHeight();

    // Re-measure on layout shifts.
    const ro = new ResizeObserver(sendHeight);
    ro.observe(document.body);

    // Catch lazy-loaded images changing height.
    const onImgLoad = () => sendHeight();
    document.addEventListener("load", onImgLoad, true);

    window.addEventListener("load", sendHeight);
    window.addEventListener("resize", onResize);

    return () => {
      ro.disconnect();
      document.removeEventListener("load", onImgLoad, true);
      window.removeEventListener("load", sendHeight);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("click", onHashClick);
    };
  }, []);
  return null;
}
